import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Popover from '@mui/material/Popover';
import { Checkbox } from '@mui/material';
import { MinLoader } from '../loader';
import { renderColor, tagColors } from './colors';
import { CustomDelete } from '../modal';
import { httpRequestsOnSuccessActions, tagActions } from 'store';
import { canManageTags, canReadTags, getAssignedTagIds } from 'store/tags/tag.utils';
import { FindLoad, FindSuccess, Images, useModal, useWidth } from 'utils';

const CREATE_ACTION_TYPE = 'CREATE_TAG';
const EDIT_TAG_ACTION_TYPE = 'EDIT_TAG';
const DELETE_ACTION_TYPE = 'DELETE_TAG';
const REMOVE_ACTION_TYPE = 'REMOVE_TAG_FROM_MEMBER';
const ADD_TAG_TO_MEMBER = 'ADD_TAG_TO_MEMBER';

export const TagComponent = ({
    userInfo,
    module,
    requestParams,
    // Context/state only — "assignment doesn't apply here" (a deleted goal, a read-only picker).
    // Never pass a permission: TAG_MANAGE is enforced inside this component, and gating on an
    // entity's own permission would make tags need a third one.
    allowAdd = true,
    assignParams = {},
    notAllowAdd,
    notSetText,
    singleView,
    doNotCropTags,
    // maxTags = 5,
    maxTags,
    afterSuccess,
}) => {
    const { tagList, tagListByModule } = useSelector((state) => ({
        tagList: state.tags.tagList,
        tagListByModule: state.tags.tagListByModule,
    }));
    const moduleTagList = tagListByModule?.[module] ?? tagList;
    const effectiveRequestParams = singleView ? { singleView: true, id: userInfo?.id } : requestParams;
    const [anchorEl, setAnchorEl] = useState(null);
    const openAnchor = Boolean(anchorEl);
    const id = openAnchor ? 'simple-popover' : undefined;
    const dispatch = useDispatch();
    const createLoad = FindLoad(CREATE_ACTION_TYPE);
    const createSuccess = FindSuccess(CREATE_ACTION_TYPE);
    const deleteLoad = FindLoad(DELETE_ACTION_TYPE);
    const deleteSuccess = FindSuccess(DELETE_ACTION_TYPE);
    const removeSuccess = FindSuccess(REMOVE_ACTION_TYPE);
    const editTagLoader = FindLoad(EDIT_TAG_ACTION_TYPE);
    const editTagSuccess = FindSuccess(EDIT_TAG_ACTION_TYPE);
    const addTagLoader = FindLoad(ADD_TAG_TO_MEMBER);
    const addTagSuccess = FindSuccess(ADD_TAG_TO_MEMBER);
    const [newTag, setNewTag] = useState('');
    const [editTag, setEditTag] = useState('');
    const [selectedIds, setSelectedId] = useState([]);
    const [filteredList, setFilteredList] = useState([]);
    const checkExistTag = moduleTagList?.find((i) => i?.name === newTag);
    const { open } = useModal();
    const width = useWidth();
    const inputRef = useRef(null);
    const [randomColor, setRandomColor] = useState();
    // TAG_READ grants VIEWING only — it must never unlock create/edit/delete/assign, or the UI
    // offers controls the API answers with 403. TAG_MANAGE implies read.
    // Mirrors api/src/tag/tag.access.ts.
    const canManage = canManageTags();
    const canRead = canReadTags();
    const viewOnly = !canManage;

    useEffect(() => {
        setRandomColor(Math.floor(Math.random() * tagColors.length));
    }, [openAnchor]);

    // Single-entity detail pages (e.g. SingleResourceHeader) don't load the catalog themselves.
    // Gated on read, not manage — listing tags is a read action.
    useEffect(() => {
        if (singleView && module && canRead) {
            dispatch(tagActions.getTags({ module }));
        }
    }, [singleView, module, canRead, dispatch]);

    useEffect(() => {
        if (deleteSuccess?.length) {
            setEditTag('');
            dispatch(httpRequestsOnSuccessActions.removeSuccess(DELETE_ACTION_TYPE));
        }
        if (removeSuccess?.length) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(REMOVE_ACTION_TYPE));
        }
        if (createSuccess?.length) {
            setNewTag('');
            dispatch(httpRequestsOnSuccessActions.removeSuccess(CREATE_ACTION_TYPE));
        }
        if (editTagSuccess?.length) {
            setEditTag('');
            setNewTag('');
            dispatch(httpRequestsOnSuccessActions.removeSuccess(EDIT_TAG_ACTION_TYPE));
        }
        if (addTagSuccess?.length) {
            setEditTag('');
            setNewTag('');
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ADD_TAG_TO_MEMBER));
            setAnchorEl(null);
            if(afterSuccess){
                afterSuccess()
            }
        }
    }, [deleteSuccess, removeSuccess, createSuccess, editTagSuccess, addTagSuccess]);

    useEffect(() => {
        if (userInfo?.tags?.length) {
            const initialSelected = getAssignedTagIds(userInfo);

            setSelectedId(initialSelected);
        }
    }, [userInfo?.tags, anchorEl]);

    useEffect(() => {
        const filtered = newTag
            ? moduleTagList?.filter((el) => el?.name?.toString().toLowerCase().includes(newTag?.toString().toLowerCase()))
            : moduleTagList;
        setFilteredList(filtered);
    }, [newTag, moduleTagList]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setEditTag(null);
        setNewTag(null);
    };

    // Single source for the /tags/assign body. `assignParams` carries the Document module's
    // resourceModel/resourceId — drop it and the API throws "Unsupported document model", so both
    // the Done button and the Create button must go through here.
    const buildAssignBody = (tagIds) => ({
        tagIds,
        model: module,
        ...assignParams,
    });

    const createTag = () => {
        if (viewOnly) return;

        const tag = {
            name: newTag,
            color: tagColors[randomColor].color,
            module: module,
        };
        // Creating from an entity's popover also assigns, on the live selection — same as Done.
        const assignBody = userInfo?.id ? buildAssignBody(selectedIds) : null;
        dispatch(tagActions.createTag(tag, { module: module }, userInfo, effectiveRequestParams, assignBody));
    };

    useEffect(() => {
        if (userInfo?.tags?.length && moduleTagList?.length) {
            const initialSelected = getAssignedTagIds(userInfo);

            setSelectedId(initialSelected);
        }
    }, [userInfo?.tags, moduleTagList, anchorEl]);

    useEffect(() => {
        const filtered = newTag
            ? moduleTagList?.filter((el) => el?.name?.toString().toLowerCase().includes(newTag?.toString().toLowerCase()))
            : moduleTagList;
        setFilteredList(filtered);
    }, [newTag, moduleTagList]);

    const handleSelectTag = (item) => {
        if (viewOnly) return;

        setSelectedId((prev) => {
            if (prev.includes(item?.id)) {
                return prev.filter((id) => id !== item?.id);
            }

            // maxTags falsy (null / 0) => no limit; used where an entity may carry every tag.
            if (maxTags && (prev.length || 0) >= maxTags) {
                return prev;
            }

            return [...prev, item?.id];
        });
    };

    const handleEditTag = (event, tag) => {
        event.stopPropagation();
        event.preventDefault();
        if (viewOnly) return;

        setEditTag(tag);
    };

    const chengTagColor = (i) => {
        setEditTag({
            color: i,
            id: editTag?.id || editTag?._id,
            name: editTag?.name,
            module: module,
        });
    };

    const handleChangeTagName = (e) => {
        setEditTag({
            color: editTag?.color,
            id: editTag?.id || editTag?._id,
            name: e?.target?.value,
            module: module,
        });
    };

    const handleDelete = (item) => {
        if (viewOnly) return;

        setAnchorEl(null);
        open(
            <CustomDelete
                info="Delete this tag?"
                text="Are you sure you want to delete this tag?"
                handleDel={() => dispatch(tagActions.deleteTag(item?._id || item?.id, module, effectiveRequestParams))}
                actionType={DELETE_ACTION_TYPE}
                body={
                    <div className="deleting-client-info-box" style={{ alignItems: 'flex-start' }}>
                        <img style={{ marginTop: '3px' }} src={Images.tagUser} alt="icon" />
                        <p style={{ fontWeight: 600, fontSize: 14 }}>{editTag?.name}</p>
                    </div>
                }
            />
        );
    };

    const handleSetNewTag = (e) => {
        setNewTag(e.target.value);
    };

    const handleDone = () => {
        if (viewOnly) return;

        if (editTag) {
            if (editTag?.name) {
                dispatch(tagActions.editTag(editTag, { module: module }, effectiveRequestParams));
            } else {
                inputRef?.current?.focus();
            }
        } else {
            const body = buildAssignBody(selectedIds);

            if (userInfo?.tags?.length || !!selectedIds?.length) {
                dispatch(tagActions.addTagToMember(userInfo?.id, body, effectiveRequestParams, module));
            }
        }
    };

    // No tag permission at all => the entity's tags are not this user's to see. Rendering nothing
    // keeps the surrounding table cell / header empty rather than leaking names through the chips.
    // Declared after every hook so the hook order stays stable across permission states.
    if (!canRead) {
        return null;
    }

    return (
        <>
            <div className="table-tags-wrapper text-style" onClick={width <= 768 ? (e) => handleClick(e) : () => {}}>
                <div className="flex text-style" style={doNotCropTags ? { flexWrap: 'wrap', gap: 4, rowGap: 4 } : undefined}>
                    {allowAdd && canManage && (
                        <div className="desktop-page-view">
                            <button type="button" aria-describedby={id} onClick={handleClick} className="add-tag-button">
                                <img src={Images.addCircleBlue} alt="Plus Green" />
                            </button>
                        </div>
                    )}

                    {userInfo?.tags?.length ? (
                        userInfo?.tags?.map(
                            (i, j) =>
                                (doNotCropTags || j <= 0) && (
                                    <div
                                        key={j}
                                        className="table-tag-chip"
                                        style={{
                                            background: renderColor(i?.color).background,
                                            color: renderColor(i?.color).color,
                                        }}
                                    >
                                        {i?.name}
                                    </div>
                                )
                        )
                    ) : (
                        <p style={{ whiteSpace: 'nowrap', fontSize: 14, color: '#84949f' }}>{notSetText || ''}</p>
                    )}

                    {!doNotCropTags && userInfo?.tags?.length > 1 ? (
                        <button
                            type="button"
                            aria-describedby={id}
                            // `allowAdd={false}` keeps the editable popover shut for managers, but must
                            // not stop a view-only user expanding +N — their popover is read-only anyway.
                            onClick={allowAdd || viewOnly ? handleClick : null}
                            className="table-tag-count-box"
                        >
                            +{userInfo?.tags?.length - 1}
                        </button>
                    ) : (
                        ''
                    )}
                </div>
            </div>

            <Popover
                id={id}
                open={openAnchor}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                <div className={`tag-fragment-wrapper${viewOnly ? ' view-only-tags' : ''}`}>
                    <div className="tag-fragment-header-box">
                        <p>{viewOnly ? 'Tags' : newTag ? 'Create Tag' : editTag ? `Edit ${editTag?.name}` : 'Tags'}</p>

                        {!notAllowAdd && !viewOnly && (
                            <button
                                type="button"
                                onClick={handleDone}
                                disabled={
                                    addTagLoader?.length
                                        ? true
                                        : createLoad?.length
                                          ? true
                                          : userInfo?.tags?.length
                                            ? false
                                            : !selectedIds?.length && !editTag
                                }
                                style={{
                                    opacity:
                                        createLoad?.length || addTagLoader?.length
                                            ? 0.6
                                            : userInfo?.tags?.length
                                              ? 1
                                              : !editTag && !selectedIds?.length
                                                ? 0.6
                                                : 1,
                                }}
                            >
                                {editTagLoader?.length || addTagLoader?.length ? (
                                    <MinLoader color={'#0C66E4'} height={'16px'} width={'16px'} position={'relative'} margin={'0'} />
                                ) : (
                                    'Done'
                                )}
                            </button>
                        )}
                    </div>
                    {viewOnly ? (
                        <div className="tag-fragment-body">
                            <div className="tag-items-wrapper">
                                {userInfo?.tags?.length ? (
                                    userInfo.tags.map((i, j) => (
                                        <div key={j} className="created-tags-wrapper">
                                            <div className="created-tags-checkbox-name">
                                                <div
                                                    className="created-tags"
                                                    style={{
                                                        background: renderColor(i?.color).background,
                                                        color: renderColor(i?.color).color,
                                                    }}
                                                >
                                                    {i?.name}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <p className="select-option">No tags assigned</p>
                                )}
                            </div>
                        </div>
                    ) : editTag ? (
                        <div className="edit-tag-wrapper">
                            <div className="edit-tag-input-wrapper">
                                <div className="flex-align-center full-width" style={{ gap: 8 }}>
                                    <div className="tag-color-box">
                                        <div style={{ background: renderColor(editTag?.color).color }} />
                                    </div>
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        style={{ color: renderColor(editTag?.color).color }}
                                        className="edit-tag-input"
                                        value={editTag?.name}
                                        onChange={handleChangeTagName}
                                        maxLength={15}
                                    />
                                    <p className="tag-limit-count">{`${editTag?.name?.length}/15`}</p>
                                </div>
                            </div>

                            <button type="button" onClick={() => handleDelete(editTag)} className="delete-tag-wrapper">
                                {deleteLoad?.length ? (
                                    <MinLoader color={'#222222CC'} />
                                ) : (
                                    <>
                                        <img src={Images.deleteTag} alt="delete" />
                                        <p>Delete tag</p>
                                    </>
                                )}
                            </button>
                            <p className="choose-tag-color">Choose tag color</p>
                            <div className="color-cards-wrapper">
                                {tagColors?.map((i, j) => (
                                    <button
                                        type="button"
                                        onClick={() => chengTagColor(i?.background)}
                                        key={j}
                                        className={editTag?.color === i?.background ? 'current-color-card' : 'color-card'}
                                    >
                                        <div style={{ background: i?.color }} />
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="tag-fragment-header">
                                <div className="space-between" style={{ padding: '0 16px 0 0' }}>
                                    <input
                                        placeholder="Add tags"
                                        className="add-tag-input"
                                        type="text"
                                        onChange={handleSetNewTag}
                                        value={newTag}
                                        maxLength={15}
                                    />
                                    {newTag && <p className="tag-limit-count">{`${newTag ? newTag?.length : 0}/15`}</p>}
                                    {newTag && (
                                        <button type="button" onClick={() => setNewTag('')} className="remove-tag-input-value">
                                            <img src={Images.remove} alt="icon" />
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="tag-fragment-body">
                                <p className="select-option">Select an option or create one</p>
                                <div className="tag-items-wrapper">
                                    {filteredList?.length
                                        ? // [...filteredList]?.sort((a, b) => {
                                          //     const aSelected = selectedIds.includes(a?.id);
                                          //     const bSelected = selectedIds.includes(b?.id);
                                          //     return aSelected === bSelected ? 0 : aSelected ? -1 : 1;
                                          // })?.map((i, j) => (
                                          //     <div
                                          //         key={j}
                                          //         className="created-tags-wrapper"
                                          //         onClick={() => handleSelectTag(i)}
                                          //     >
                                          //         <div className="created-tags-checkbox-name">
                                          //             <Checkbox
                                          //                 style={{ color: '#347AF0', padding: '0' }}
                                          //                 checked={selectedIds?.includes(i?.id)}
                                          //                 value={i?.id}
                                          //             />
                                          //
                                          //             <div
                                          //                 className="created-tags"
                                          //                 style={{
                                          //                     background: renderColor(i?.color).background,
                                          //                     color: renderColor(i?.color).color,
                                          //                 }}
                                          //             >
                                          //                 {i?.name}
                                          //             </div>
                                          //         </div>
                                          //
                                          //         <button
                                          //             onClick={(e) => handleEditTag(e, i)}
                                          //             className="tag-edit-button"
                                          //         >
                                          //             <img src={Images.moreGray} alt="icon" />
                                          //         </button>
                                          //     </div>
                                          // ))
                                          [...filteredList]
                                              ?.sort((a, b) => {
                                                  const aSelected = selectedIds.includes(a?.id);
                                                  const bSelected = selectedIds.includes(b?.id);
                                                  if (aSelected !== bSelected) return aSelected ? -1 : 1;
                                                  return a?.name?.localeCompare(b?.name, undefined, { sensitivity: 'base' });
                                              })
                                              ?.map((i, j) => (
                                                  <div key={j} className="created-tags-wrapper" onClick={() => handleSelectTag(i)}>
                                                      <div className="created-tags-checkbox-name">
                                                          {!notAllowAdd && (
                                                              <Checkbox
                                                                  disabled={createLoad?.length || addTagLoader?.length}
                                                                  style={{
                                                                      color: '#347AF0',
                                                                      padding: '0',
                                                                      opacity: createLoad?.length || addTagLoader?.length ? 0.6 : 1,
                                                                  }}
                                                                  checked={selectedIds?.includes(i?.id)}
                                                                  value={i?.id}
                                                              />
                                                          )}
                                                          <div
                                                              className="created-tags"
                                                              style={{
                                                                  background: renderColor(i?.color).background,
                                                                  color: renderColor(i?.color).color,
                                                              }}
                                                          >
                                                              {i?.name}
                                                          </div>
                                                      </div>

                                                      <button
                                                          type="button"
                                                          disabled={createLoad?.length || addTagLoader?.length}
                                                          onClick={(e) => handleEditTag(e, i)}
                                                          className="tag-edit-button"
                                                      >
                                                          <img src={Images.moreGray} alt="icon" />
                                                      </button>
                                                  </div>
                                              ))
                                        : ''}
                                </div>

                                {newTag && !checkExistTag && (
                                    <button
                                        type="button"
                                        onClick={createTag}
                                        className="create-tag-button"
                                        disabled={createLoad?.length || addTagLoader?.length}
                                        style={{
                                            border: '1px solid #0C66E3',
                                            background: '#F5F9FE',
                                            color: '#0C66E3',
                                            opacity: createLoad?.length || addTagLoader?.length ? 0.6 : 1,
                                        }}
                                    >
                                        {createLoad?.length ? (
                                            <MinLoader color="#0C66E3" />
                                        ) : (
                                            <>
                                                <p>Create</p>
                                                <div
                                                    style={{
                                                        background: tagColors[randomColor].background,
                                                        color: tagColors[randomColor].color,
                                                    }}
                                                    className="create-tag-text"
                                                >
                                                    {' '}
                                                    {newTag}{' '}
                                                </div>
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </Popover>
        </>
    );
};
